import React from 'react';
import { CollapsableCard } from '@app/app/_components/CollapsableCard/CollapsableCard';
import { FormState } from '@app/lib/form/contracts/FormState';
import { useLocale, useTranslations } from 'next-intl';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { CheckboxInput } from '@app/app/_components/Input/CheckboxInput';
import { ExpandableText } from '@app/app/_components/ExpandableText/ExpandableText';
import Link from 'next/link';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { InsuredPeopleSummaryCard } from '@app/app/esales/personal-accidents/_sections/steps/SummaryStep/InsuredPeopleSummaryCard';
import { PurchasePersonalInsuranceResult } from '@app/service/eshop/personal-accidents/actions/purchasePersonalAccidentsInsurance';
import { PersonalAccidentsInsuranceRequestResult } from '@app/service/eshop/personal-accidents/actions/requestPersonalAccidentsInsurance';
import { PolicyholderFormFields } from '@app/app/esales/personal-accidents/_sections/PersonalAccidentsWizardSection/hooks/usePolicyholderFormFields';
import { InsuredPerson } from '@app/domain/model/eshop/personal-accidents/InsuredPerson';
import { PolicyholderSummaryCard } from '@app/app/esales/personal-accidents/_sections/steps/SummaryStep/PolicyholderSummaryCard';
import { PersonalInsuranceRiskPackage } from '@app/domain/model/eshop/personal-accidents/PersonalInsuranceRiskPackage';
import { InsuranceConditionsPanel } from '@app/app/esales/personal-accidents/_sections/steps/SummaryStep/InsuranceConditionsPanel';
import { PaymentFrequencyPanel } from '@app/app/esales/personal-accidents/_sections/steps/SummaryStep/PaymentFrequencyPanel';
import { InsuranceStartDatePanel } from '@app/app/esales/personal-accidents/_sections/steps/SummaryStep/InsuranceStartDatePanel';

interface Props {
    formState: FormState<PurchasePersonalInsuranceResult>;
    requestResult: Nullable<PersonalAccidentsInsuranceRequestResult>;
    policyholderFields: PolicyholderFormFields;
    personalCode: string;
    onSavePolicyholder: (data: PolicyholderFormFields) => void;
    insuredPersons: InsuredPerson[];
    onChangeNumberOfPersons: () => void;
    editPolicyholderInfo: () => void;
    editInsuredPeople: () => void;
    editPolicy: () => void;
    editBeginDate: () => void;
    selectedRisk: Nullable<PersonalInsuranceRiskPackage>;
    totalPrice: number;
    priceToPayNow: number;
    paymentFrequency: number;
    onPaymentFrequencyChange: (paymentFrequency: number) => void;
    hidePaymentFrequency?: boolean;
}

export function SummaryStep({
    formState,
    requestResult,
    policyholderFields,
    personalCode,
    onSavePolicyholder,
    insuredPersons,
    editInsuredPeople,
    editPolicy,
    editBeginDate,
    selectedRisk,
    totalPrice,
    priceToPayNow,
    paymentFrequency,
    onPaymentFrequencyChange,
    hidePaymentFrequency = false,
}: Props) {
    const t = useTranslations();
    const locale = useLocale();
    const parameters = useAppParameters();

    if (requestResult == null) {
        return null;
    }

    return (
        <div className="flex flex-col gap-4">
            <CollapsableCard defaultCollapsed={false} label={t('eshop.personalAccidents.summaryStep.insuredPersons')}>
                <InsuredPeopleSummaryCard insuredPersons={insuredPersons} editInsuredPeople={editInsuredPeople} />
            </CollapsableCard>

            <CollapsableCard
                defaultCollapsed={false}
                label={t('eshop.personalAccidents.summaryStep.policyholderInformation')}>
                <PolicyholderSummaryCard
                    policyholderFields={policyholderFields}
                    formState={formState}
                    personalCode={personalCode}
                    onSave={onSavePolicyholder}
                />
            </CollapsableCard>

            {selectedRisk && (
                <CollapsableCard defaultCollapsed={false} label={t('eshop.personalAccidents.policy')}>
                    <InsuranceConditionsPanel selectedRisk={selectedRisk} totalPrice={totalPrice} onEdit={editPolicy} />

                    {!hidePaymentFrequency && (
                        <PaymentFrequencyPanel
                            totalPrice={totalPrice}
                            priceToPayNow={priceToPayNow}
                            paymentFrequency={paymentFrequency}
                            onPaymentFrequencyChange={onPaymentFrequencyChange}
                        />
                    )}

                    <InsuranceStartDatePanel
                        beginDate={formState.data?.formData.beginDate ?? ''}
                        onEdit={editBeginDate}
                    />
                </CollapsableCard>
            )}

            <div className="mt-6 flex flex-col gap-4">
                <CheckboxInput
                    name="acceptToPrivacyPolicy"
                    error={formState.violations?.acceptToPrivacyPolicy}
                    label={t.rich('eshop.personalAccidents.summaryStep.privacyPolicyConsentLabel', {
                        link: chunks => (
                            <Link
                                className="underline"
                                href={
                                    locale === 'lt'
                                        ? parameters.eshopFooterUrls.privacyPolicy
                                        : parameters.eshopFooterUrls.privacyPolicyEn
                                }
                                target="_blank">
                                {chunks}
                            </Link>
                        ),
                    })}
                />

                <CheckboxInput
                    name="acceptToPaRules"
                    error={formState.violations?.acceptToPaRules}
                    label={t.rich('eshop.personalAccidents.summaryStep.paRulesConsentLabel', {
                        link: chunks => (
                            <Link
                                className="underline"
                                href={
                                    locale === 'lt'
                                        ? parameters.eshopPersonalAccidentsInfoUrls.paInsuranceRules
                                        : parameters.eshopPersonalAccidentsInfoUrls.paInsuranceRulesEn
                                }
                                target="_blank">
                                {chunks}
                            </Link>
                        ),
                    })}
                />

                <ExpandableText className="text-gray-700 text-sm">
                    <div className="whitespace-pre-line">
                        {t.rich('eshop.personalAccidents.summaryStep.directMarketingInfoText', {
                            compensaEmail: chunks => (
                                <Link
                                    href="mailto:tiesioginerinkodara@compensa.lt"
                                    className="hover:text-blue-800 text-blue-600 underline"
                                    target="_blank">
                                    {chunks}
                                </Link>
                            ),
                            compensaLifeEmail: chunks => (
                                <Link
                                    href="mailto:tiesioginerinkodara@compensalife.lt"
                                    className="hover:text-blue-800 text-blue-600 underline"
                                    target="_blank">
                                    {chunks}
                                </Link>
                            ),
                            privacyPolicyLink: chunks => (
                                <Link
                                    href={
                                        locale === 'lt'
                                            ? parameters.eshopFooterUrls.privacyPolicy
                                            : parameters.eshopFooterUrls.privacyPolicyEn
                                    }
                                    className="hover:text-blue-800 text-blue-600 underline"
                                    target="_blank">
                                    {chunks}
                                </Link>
                            ),
                        })}
                    </div>
                </ExpandableText>

                <div className="flex flex-col gap-4">
                    <Paragraph isBold>{t('eshop.personalAccidents.summaryStep.directMarketingAgreement')}</Paragraph>
                    <CheckboxInput
                        name="acceptToMarketingCnl"
                        label={t('eshop.personalAccidents.summaryStep.agreeToMarketingCompensa')}
                    />
                    <CheckboxInput
                        name="acceptToMarketingCl"
                        label={t('eshop.personalAccidents.summaryStep.agreeToMarketingCompensaLife')}
                    />
                </div>
            </div>
        </div>
    );
}
